import { Injectable } from '@angular/core';
import {
    FlyFreelyLoggingService,
    LocationFeatureDto
} from '@flyfreely-portal-ui/flyfreely';
import { Feature, Geometry, GeometryCollection } from 'geojson';
import { buildAreaLineFeature } from 'libs/map/src/lib/draw-modes/draw-area-line';
import { bufferPolygon } from 'libs/map/src/lib/utils';

export const locationFeatureTypes = [
    { value: 'AREA_OF_INTEREST', name: 'Area Of Interest' },
    { value: 'DANGER', name: 'Danger' },
    { value: 'FLIGHT_AREA', name: 'Flight Area' },
    { value: 'FLIGHT_PATH_LINE', name: 'Flight Path Line' },
    { value: 'GENERAL_LINE', name: 'General Line' },
    { value: 'LANDING', name: 'Landing' },
    { value: 'NO_FLY', name: 'No Fly' },
    { value: 'OBSERVER', name: 'Observer' },
    { value: 'POINT_OF_INTEREST', name: 'Point Of Interest' },
    { value: 'RP', name: 'RP' },
    { value: 'TAKEOFF', name: 'Takeoff' },
    { value: 'TAKEOFF_LANDING', name: 'Takeoff/Landing' },
    { value: 'OFFSET_FLIGHT_AREA', name: 'Corridor Flight Area' },
    { value: 'OFFSET_DANGER_AREA', name: 'Corridor Danger Area' },
    { value: 'OFFSET_NO_FLY_AREA', name: 'Corridor No Fly Area' }
];

export interface UploadedFeature {
    featureType: LocationFeatureDto.Type;
    name: string;
    geometry: Geometry;
    properties?: any;
}

export const corridorFeatureTypes: LocationFeatureDto.Type[] = [
    LocationFeatureDto.Type.OFFSET_FLIGHT_AREA,
    LocationFeatureDto.Type.OFFSET_DANGER_AREA,
    LocationFeatureDto.Type.OFFSET_NO_FLY_AREA
];

export interface DefaultKmlFeatureTypesByGeometryType {
    polygon: LocationFeatureDto.Type;
    point: LocationFeatureDto.Type;
    lineString: LocationFeatureDto.Type;
}

const defaultKmlFeatureTypesByGeometryType: DefaultKmlFeatureTypesByGeometryType =
    {
        polygon: LocationFeatureDto.Type.FLIGHT_AREA,
        point: LocationFeatureDto.Type.POINT_OF_INTEREST,
        lineString: LocationFeatureDto.Type.FLIGHT_PATH_LINE
    };

export interface KmlFeatureGroups {
    type:
        | 'Point'
        | 'MultiPoint'
        | 'LineString'
        | 'MultiLineString'
        | 'Polygon'
        | 'MultiPolygon'
        | 'GeometryCollection';
    featureType: 'Areas' | 'Markers' | 'Lines' | 'Corridors';
    features: UploadedFeature[];
}

export function isSinglePolygon(merged: KmlFeatureGroups[]) {
    return (
        merged.length === 1 &&
        merged[0].type === 'Polygon' &&
        merged[0].features.length === 1
    );
}

/**
 * Convert an uploaded LineString feature into a corridor feature
 */
export function transformLineToCorridor(
    feature: UploadedFeature
): UploadedFeature | null {
    if (
        feature.geometry?.type !== 'LineString' ||
        feature.geometry.coordinates.length < 2
    ) {
        // createBufferFromLine returns null for lines with fewer than 2 points
        return null;
    }
    const corridor = buildAreaLineFeature(feature.geometry, {
        lineOffsetMeters: feature.properties?.lineOffsetMeters ?? 50
    });
    return {
        featureType: feature.featureType,
        name: feature.name,
        geometry: corridor.geometry,
        properties: corridor.properties
    };
}

/**
 * Buffer an uploaded Polygon feature by the given distance; a negative
 * distance shrinks it inward. Returns null when the geometry isn't a polygon
 * or the buffer would destroy the shape, leaving the caller to keep the
 * original. The bufferMeters property is intentionally left untouched here -
 * it's a one-time import setting that the caller is responsible for stripping
 * before persisting the feature.
 */
export function bufferAreaFeature(
    feature: UploadedFeature,
    distanceMeters: number
): UploadedFeature | null {
    if (feature.geometry?.type !== 'Polygon') {
        return null;
    }
    const buffered = bufferPolygon(feature.geometry, distanceMeters);
    if (buffered == null) {
        return null;
    }
    return {
        featureType: feature.featureType,
        name: feature.name,
        geometry: buffered.geometry,
        properties: feature.properties
    };
}

@Injectable()
export class KmlUploadService {
    constructor(private logging: FlyFreelyLoggingService) {}

    parseKmlGroupings(
        featureCollection: GeoJSON.FeatureCollection,
        defaultFeatureTypes?: DefaultKmlFeatureTypesByGeometryType
    ) {
        return this.extractFeatureGroups(
            featureCollection.features,
            null,
            defaultFeatureTypes
        );
    }

    private extractFeatureGroups(
        importFeatures: Feature[],
        featureNames?: string[],
        defaultFeatureTypes: DefaultKmlFeatureTypesByGeometryType = defaultKmlFeatureTypesByGeometryType
    ) {
        const extractedGroups: KmlFeatureGroups[] = [];
        const features = importFeatures.filter(
            f => f != null && f.geometry != null
        );
        const failedLength = importFeatures.length - features.length;
        const pushToExtracted = (
            type:
                | 'Point'
                | 'MultiPoint'
                | 'LineString'
                | 'MultiLineString'
                | 'Polygon'
                | 'MultiPolygon'
                | 'GeometryCollection',
            feats: UploadedFeature[]
        ) => {
            const i = extractedGroups.findIndex(g => g.type === type);
            if (i === -1) {
                extractedGroups.push({
                    type: type,
                    featureType: this.findFeatureType(type),
                    features: feats
                });
            } else {
                if (extractedGroups[i].features.length > 0) {
                    extractedGroups[i].features =
                        extractedGroups[i].features.concat(feats);
                } else {
                    extractedGroups[i].features = feats;
                }
            }
        };

        const types = features.reduce(
            (acc, g) =>
                acc.includes(g.geometry.type)
                    ? acc
                    : acc.concat(g.geometry.type),
            []
        );

        types.forEach(t => {
            if (
                t === 'MultiPolygon' ||
                t === 'MultiPoint' ||
                t === 'MultiLineString'
            ) {
                const geometryType =
                    t === 'MultiPolygon'
                        ? 'Polygon'
                        : t === 'MultiPoint'
                        ? 'Point'
                        : 'LineString';
                const feats = features
                    .filter(f => f.geometry.type === t)
                    .reduce(
                        (acc, f, i) =>
                            acc.concat(
                                this.extractMulti(f, i, defaultFeatureTypes)
                            ),
                        []
                    );
                return pushToExtracted(geometryType, feats);
            } else if (t === 'GeometryCollection') {
                const groups = features.filter(
                    f => f.geometry.type === 'GeometryCollection'
                );
                groups.forEach((f: Feature, index) => {
                    const propertyType: LocationFeatureDto.Type =
                        f.properties?.type;
                    // Corridors are exported as a GeometryCollection (buffer
                    // polygon + centerline). Keep them intact as a single
                    // corridor feature rather than splitting the collection
                    // into separate polygon/line features.
                    if (
                        propertyType != null &&
                        corridorFeatureTypes.includes(propertyType)
                    ) {
                        return pushToExtracted('GeometryCollection', [
                            this.mapCorridorFeature(f, index)
                        ]);
                    }
                    const collection: GeometryCollection = <GeometryCollection>(
                        f.geometry
                    );
                    const geomFeatures: Feature[] = collection.geometries.map(
                        g => ({
                            ...f,
                            geometry: g
                        })
                    );
                    const extracted = this.extractFeatureGroups(geomFeatures);
                    extracted.forEach(item =>
                        pushToExtracted(item.type, item.features)
                    );
                });
            } else {
                const feats = features
                    .filter(f => f.geometry.type === t)
                    .map((f, i) => this.mapFeatures(f, i, defaultFeatureTypes));
                return pushToExtracted(t, feats);
            }
        });

        if (failedLength > 0) {
            this.logging.error(
                null,
                `Error importing ${failedLength} features. Please verify the uploaded file is correct and try again.`
            );
        }

        return extractedGroups;
    }

    findFeatureType(
        geometryType:
            | 'Point'
            | 'MultiPoint'
            | 'LineString'
            | 'MultiLineString'
            | 'Polygon'
            | 'MultiPolygon'
            | 'GeometryCollection'
    ) {
        switch (geometryType) {
            case 'Polygon':
            case 'MultiPolygon':
                return 'Areas';

            case 'LineString':
            case 'MultiLineString':
                return 'Lines';

            case 'Point':
            case 'MultiPoint':
                return 'Markers';

            case 'GeometryCollection':
                return 'Corridors';

            default:
                return 'Areas';
        }
    }

    private mapFeatures(
        feature: Feature,
        index: number,
        defaultFeatureTypes: DefaultKmlFeatureTypesByGeometryType
    ) {
        const propertyType: LocationFeatureDto.Type = feature.properties?.type;
        const type: LocationFeatureDto.Type =
            propertyType != null &&
            locationFeatureTypes.some(t => t.value === propertyType)
                ? propertyType
                : this.deriveFeatureTypeFromGeometry(
                      feature,
                      defaultFeatureTypes
                  );
        const featureName = locationFeatureTypes.find(
            t => t.value === type
        ).name;
        return {
            name:
                feature.properties?.name == null
                    ? `${featureName} ${index + 1}`
                    : feature.properties?.name,
            featureType: type,
            geometry: feature.geometry
        };
    }

    /**
     * Map a corridor feature, keeping its GeometryCollection geometry intact and
     * preserving type specific properties
     */
    private mapCorridorFeature(
        feature: Feature,
        index: number
    ): UploadedFeature {
        const type: LocationFeatureDto.Type = feature.properties?.type;
        const featureName = locationFeatureTypes.find(
            t => t.value === type
        )?.name;
        return {
            name:
                feature.properties?.name == null
                    ? `${featureName} ${index + 1}`
                    : feature.properties?.name,
            featureType: type,
            geometry: feature.geometry,
            properties: feature.properties
        };
    }

    /**
     * Derive the feature type from its geometry type and name. Used as a
     * fallback for KMLs that don't carry an explicit `type` property (older
     * exports, or files created in third-party software).
     */
    private deriveFeatureTypeFromGeometry(
        feature: Feature,
        defaultFeatureTypes: DefaultKmlFeatureTypesByGeometryType
    ): LocationFeatureDto.Type {
        let type: LocationFeatureDto.Type;
        const name: string = feature.properties?.name;
        // Try to derive type from feature name, otherwise use a generic type
        switch (feature.geometry.type) {
            case 'Polygon':
                type = defaultFeatureTypes?.polygon;
                if (name != null) {
                    // Danger seems to be the only real flight area type that will have a unique name apart from the default
                    if (name.toLowerCase().includes('danger')) {
                        type = LocationFeatureDto.Type.DANGER;
                    } else if (name.toLowerCase().includes('no fly')) {
                        type = LocationFeatureDto.Type.NO_FLY;
                    } else if (
                        name.toLowerCase().includes('area of interest')
                    ) {
                        type = LocationFeatureDto.Type.AREA_OF_INTEREST;
                    }
                }
                break;

            case 'LineString':
                type = defaultFeatureTypes.lineString;
                break;

            case 'Point':
                type = defaultFeatureTypes.point;
                if (name != null) {
                    if (
                        name.toLowerCase().includes('takeoff') ||
                        name.toLowerCase().includes('take-off') ||
                        name.toLowerCase().includes('take off')
                    ) {
                        type = LocationFeatureDto.Type.TAKEOFF;
                    }
                    if (name.toLowerCase().includes('landing')) {
                        type = LocationFeatureDto.Type.LANDING;
                    }
                    if (name.toLowerCase().includes('pilot')) {
                        type = LocationFeatureDto.Type.RP;
                    }
                    if (name.toLowerCase().includes('observer')) {
                        type = LocationFeatureDto.Type.OBSERVER;
                    }
                }
                break;

            default:
                type = defaultFeatureTypes.polygon;
                break;
        }
        return type;
    }

    private extractMulti(
        feature: Feature,
        index: number,
        defaultFeatureTypes: DefaultKmlFeatureTypesByGeometryType
    ) {
        let type: LocationFeatureDto.Type;
        switch (feature.geometry.type) {
            case 'MultiPolygon':
                type = defaultFeatureTypes.polygon;
                break;

            case 'MultiLineString':
                type = defaultFeatureTypes.lineString;
                break;

            case 'MultiPoint':
                type = defaultFeatureTypes.point;
                break;

            default:
                type = defaultFeatureTypes.polygon;
                break;
        }
        const featureName = locationFeatureTypes.find(
            t => t.value === type
        ).name;
        const geometryType =
            feature.geometry.type === 'MultiPolygon'
                ? 'Polygon'
                : feature.geometry.type === 'MultiPoint'
                ? 'Point'
                : 'LineString';
        if (
            feature.geometry.type === 'MultiPolygon' ||
            feature.geometry.type === 'MultiPoint' ||
            feature.geometry.type === 'MultiLineString'
        ) {
            const positionSets = feature.geometry.coordinates;
            // @ts-ignore - function incorrectly flags as not callable
            return <UploadedFeature[]>positionSets.map((p, i) => ({
                name:
                    feature.properties?.name == null
                        ? `Multi ${featureName} ${index + i + 1}`
                        : `${feature.properties?.name} ${index + i + 1}`,
                featureType: type,
                geometry: {
                    type: geometryType,
                    coordinates: p
                }
            }));
        }
    }
}
